import { useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setCredentials } from "state/authSlice";

// A sign-in that gets no answer must end, and must say why. With no ceiling a
// connection that never answers kept "Signing in…" up for as long as the phone
// cared to wait, and a failure with no server answer threw inside the catch
// (`error.response` is undefined), so no message ever showed (2026-09-27,
// Salma PH: her requests never reached the server at all).
export const LOGIN_TIMEOUT_MS = 15000

// A sign-in that got NO answer — the connection failed, or the ceiling above
// cut it off — is tried again before anyone is told, with these pauses
// between tries (2026-09-28, Salma PH on 2 bars of 4G: her 13:36 tap never
// reached the server; her 13:57 tap did and everything worked). A refusal
// from the server is final and never repeated. Mutable so a test can shrink
// the pauses.
export const loginRetry = { delaysMs: [1000, 3000] }

const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"]

const gotNoAnswer = (error) => !error?.response

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// One sign-in request, repeated while it gets no answer and tries remain.
const postLogin = async (email, password) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await axios.post(
        `${process.env.REACT_APP_BASE_URL}usersServer/login`,
        { email, password },
        { timeout: LOGIN_TIMEOUT_MS }
      )
    } catch (error) {
      const delay = loginRetry.delaysMs[attempt]
      if (!gotNoAnswer(error) || delay === undefined) throw error
      await wait(delay)
    }
  }
}

// What the Login page shows ({ message }) for a failed sign-in.
const loginFailure = (error) => {
  const data = error?.response?.data
  if (data && typeof data === "object" && data.message) return data
  if (TIMEOUT_CODES.includes(error?.code)) {
    return {
      message:
        "Signing in is taking too long. Check your internet connection and try again.",
    }
  }
  if (!error?.response) {
    return {
      message:
        "Can't reach Phieg. Check your internet connection (try Wi-Fi or mobile data) and try again.",
    }
  }
  // The server answered, but not with one of its own messages (a proxy page).
  return { message: "Phieg couldn't sign you in right now. Please try again in a minute." }
}

export const useLogin = () => {
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(null)
  const [checkError, setCheckError] = useState(false)

  const dispatch = useDispatch()
  
  const login = async (email, password) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await postLogin(email, password)
      const { user, token } = await response.data
      
      // save user to local storage
      localStorage.setItem("user", JSON.stringify({user, token}))

      // update the authcontext
      dispatch(setCredentials({ user, token }))

      setIsLoading(false)
      setCheckError(true)
    } catch (error) {
      setIsLoading(false)
      setError(loginFailure(error))
      setCheckError(true)
    }
  }

  return { login, isLoading, error, checkError }
}